import { useEffect, useState, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  ArrowLeft, Video, Search, Play, X, Youtube as YT,
  Facebook as FB, ExternalLink, Loader2, Filter, Tv, Edit3, Plus, Save,
  Share2, Check as CheckIcon
} from 'lucide-react';
import VintageTV from './VintageTV';
import InlineAdminGate from './InlineAdminGate';
import InlineArtistEditor from './InlineArtistEditor';
import InlineVideoEditor from './InlineVideoEditor';
import useInlineAdmin from '../hooks/useInlineAdmin';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PLATFORM_META = {
  youtube:    { label: 'YouTube',    color: 'text-red-400',    bg: 'bg-red-500/15',    bd: 'border-red-500/30' },
  facebook:   { label: 'Facebook',   color: 'text-blue-400',   bg: 'bg-blue-500/15',   bd: 'border-blue-500/30' },
  soundcloud: { label: 'SoundCloud', color: 'text-orange-400', bg: 'bg-orange-500/15', bd: 'border-orange-500/30' },
  vimeo:      { label: 'Vimeo',      color: 'text-cyan-400',   bg: 'bg-cyan-500/15',   bd: 'border-cyan-500/30' },
  spotify:    { label: 'Spotify',    color: 'text-green-400',  bg: 'bg-green-500/15',  bd: 'border-green-500/30' },
  tiktok:     { label: 'TikTok',     color: 'text-pink-400',   bg: 'bg-pink-500/15',   bd: 'border-pink-500/30' },
  other:      { label: 'Lien',       color: 'text-zinc-400',   bg: 'bg-zinc-700/40',   bd: 'border-zinc-600' },
};

function getYoutubeId(url) {
  const m = (url || '').match(/(?:youtube\.com\/(?:watch\?v=|embed\/|v\/|shorts\/)|youtu\.be\/)([\w-]{11})/);
  return m?.[1] || null;
}

function getYoutubeThumb(url) {
  const id = getYoutubeId(url);
  return id ? `https://i.ytimg.com/vi/${id}/hqdefault.jpg` : null;
}

/* Inline share button — copies a deep link (or uses native share on mobile) */
function ShareVideoButton({ video }) {
  const [copied, setCopied] = useState(false);
  const url = (() => {
    if (typeof window === 'undefined') return '';
    const u = new URL(window.location.origin);
    u.pathname = '/gallery';
    u.searchParams.set('video', video.id);
    return u.toString();
  })();
  const onClick = async (e) => {
    e.stopPropagation();
    try {
      if (navigator.share) {
        await navigator.share({
          title: `${video.artistName} — ${video.title}`,
          text: `Regarde "${video.title}" par ${video.artistName} sur KAZAJAH TV`,
          url,
        });
        return;
      }
    } catch { /* user cancelled — fall through */ }
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      window.prompt('Copie ce lien :', url);
    }
  };
  return (
    <button onClick={onClick}
      data-testid={`gallery-video-share-${video.id}`}
      className="absolute bottom-2 right-2 z-20 p-1.5 rounded-lg bg-black/70 border border-yellow-500/40 text-yellow-300 hover:bg-yellow-500 hover:text-black transition-colors"
      title={copied ? 'Lien copie !' : 'Partager cette video'}>
      {copied ? <CheckIcon size={12} /> : <Share2 size={12} />}
    </button>
  );
}

export default function GalleryPage({ onBack, staticArtists = [], initialArtistFilter = 'all' }) {
  const [artists, setArtists] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [activeArtist, setActiveArtist] = useState(initialArtistFilter);
  const [activePlatform, setActivePlatform] = useState('all');
  const [playing, setPlaying] = useState(null);
  const [editingArtist, setEditingArtist] = useState(null);
  const [editingVideo, setEditingVideo] = useState(null); // { artist, videoIndex }
  const [quickAdd, setQuickAdd] = useState(false);
  const { isAdmin: isInlineAdmin, adminPwd } = useInlineAdmin();

  const fetchArtists = () => {
    fetch(`${API}/artists`).then(r => r.ok ? r.json() : [])
      .then(d => Array.isArray(d) ? d : [])
      .then(d => setArtists(d))
      .catch(() => setArtists([]))
      .finally(() => setLoading(false));
  };

  // Sync filter when prop changes (e.g. user clicks an artist photo)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { setActiveArtist(initialArtistFilter || 'all'); }, [initialArtistFilter]);

  // When the lightbox opens (user starts a video) → pause global radio.
  useEffect(() => {
    if (playing) window.dispatchEvent(new CustomEvent('kazajah:video-playing'));
  }, [playing]);

  useEffect(() => {
    fetchArtists();
  }, []);

  // Deep-link support: open ?video=<id> automatically once the video list is built.
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const target = new URLSearchParams(window.location.search).get('video');
    if (!target) return;
    // Find the video by id once allVideos is populated. We re-run when artists change.
    // (allVideos is a derived value but its memoization key changes whenever artists do.)
    // We use a tiny timeout to ensure mergedArtists has re-built.
    const t = setTimeout(() => {
      const all = [];
      // Re-implement allVideos here briefly without TV_BLOCKED filter so links to
      // SoundCloud videos still resolve via the lightbox (they will just open in a new tab).
      artists.forEach(a => {
        if (!a || !a.name) return;
        (a.videos || []).forEach((v, idx) => {
          if (!v.url) return;
          all.push({
            id: `${a.id}-${idx}`,
            artistId: a.id,
            artistName: a.name,
            videoIndex: idx,
            title: v.title || `Video ${idx + 1}`,
            url: v.url,
            platform: v.platform || 'other',
          });
        });
      });
      const found = all.find(v => v.id === target);
      if (found) setPlaying(found);
    }, 250);
    return () => clearTimeout(t);
  }, [artists]);

  // Build full video list by merging backend artists + static artists from props
  const mergedArtists = useMemo(() => {
    const merged = new Map();
    staticArtists.forEach(a => merged.set(a.id, a));
    artists.forEach(a => {
      const existing = merged.get(a.id);
      merged.set(a.id, existing ? { ...existing, ...a } : a);
    });
    return Array.from(merged.values());
  }, [artists, staticArtists]);

  const allVideos = useMemo(() => {
    const list = [];
    // Platforms NOT shown in KAZAJAH TV — TV is for video platforms only.
    // SoundCloud is audio-only and belongs to JAH RADIO, not the TV.
    const TV_BLOCKED = new Set(['soundcloud', 'spotify']);
    mergedArtists.forEach(a => {
      if (!a || !a.name) return; // skip placeholders
      (a.videos || []).forEach((v, idx) => {
        if (!v.url) return;
        const platform = v.platform || 'other';
        if (TV_BLOCKED.has(platform)) return;
        list.push({
          id: `${a.id}-${idx}`,
          artistId: a.id,
          artistName: a.name,
          artistImage: a.image,
          videoIndex: idx,
          title: v.title || `Video ${idx + 1}`,
          url: v.url,
          platform,
        });
      });
    });
    return list;
  }, [mergedArtists]);

  const artistOptions = useMemo(() => {
    const set = new Map();
    allVideos.forEach(v => { if (!set.has(v.artistId)) set.set(v.artistId, v.artistName); });
    return [['all', 'Tous les artistes'], ...Array.from(set.entries())];
  }, [allVideos]);

  const platformCounts = useMemo(() => {
    const c = {};
    allVideos.forEach(v => { c[v.platform] = (c[v.platform] || 0) + 1; });
    return c;
  }, [allVideos]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return allVideos.filter(v => {
      if (activeArtist !== 'all' && v.artistId !== activeArtist) return false;
      if (activePlatform !== 'all' && v.platform !== activePlatform) return false;
      if (!q) return true;
      return (v.title?.toLowerCase().includes(q) || v.artistName?.toLowerCase().includes(q));
    });
  }, [allVideos, search, activeArtist, activePlatform]);

  return (
    <div className="min-h-screen bg-black text-white" data-testid="gallery-page">
      {/* Inline admin */}
      <InlineAdminGate label="Gerer videos" accent="red">
        {({ isAdmin, adminPwd }) => (
          <>
            {editingArtist && (
              <InlineArtistEditor artist={editingArtist} adminPwd={adminPwd}
                onClose={() => setEditingArtist(null)}
                onSaved={() => { setEditingArtist(null); fetchArtists(); }} />
            )}
            {isAdmin && (
              <div className="bg-red-500/5 border-b border-red-500/20 pt-20 sm:pt-24 px-4 pb-3"
                data-testid="gallery-admin-panel">
                <div className="max-w-7xl mx-auto">
                  <div className="flex items-center justify-between gap-3 mb-2 flex-wrap">
                    <p className="text-[10px] uppercase tracking-widest font-bold text-red-300">
                      Mode admin — clique sur un artiste pour gerer ses videos YouTube/Facebook/Vimeo
                    </p>
                    <button onClick={() => setQuickAdd(true)}
                      data-testid="gallery-quick-add-video"
                      title="Cliquer pour ouvrir le formulaire d'ajout de video (YouTube, Facebook, Vimeo, TikTok)"
                      className="px-4 py-2.5 bg-red-500 hover:bg-red-400 text-white text-sm font-black rounded-full inline-flex items-center gap-2 shadow-lg shadow-red-500/40 cursor-pointer ring-2 ring-red-400/30 hover:ring-red-300 hover:scale-105 transition-all">
                      <Plus size={16} /> Ajouter une nouvelle video
                    </button>
                  </div>
                  <p className="text-[10px] text-zinc-500 mb-2">
                    <b className="text-zinc-300">Auto-sync :</b> toute video ajoutee a un artiste (ici ou depuis l&apos;onglet Artistes) apparait automatiquement dans KAZAJAH TV.
                  </p>
                  <div className="flex flex-wrap gap-1.5">
                    {mergedArtists.map(a => (
                      <button key={a.id} onClick={() => setEditingArtist(a)}
                        data-testid={`gallery-edit-artist-${a.id}`}
                        title={`Cliquer pour gerer les videos de ${a.name}`}
                        className="px-2.5 py-1 bg-zinc-900 hover:bg-red-500/20 hover:text-red-300 border border-zinc-800 hover:border-red-500/40 text-zinc-300 text-[11px] rounded-full transition-colors inline-flex items-center gap-1 cursor-pointer">
                        <Edit3 size={10} /> {a.name} ({(a.videos || []).length})
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </>
        )}
      </InlineAdminGate>

      {/* Hero */}
      <div className="relative overflow-hidden border-b border-zinc-900">
        <div className="absolute inset-0 bg-gradient-to-br from-yellow-900/25 via-amber-900/10 to-black" />
        <div className="absolute inset-0 opacity-30"
          style={{ backgroundImage: 'radial-gradient(circle at 30% 20%, rgba(234,179,8,0.3), transparent 55%), radial-gradient(circle at 75% 80%, rgba(249,115,22,0.2), transparent 55%)' }} />
        {/* Top padding: pt-24 when no admin band; smaller when band already accounts for the fixed nav */}
        <div className={`relative max-w-7xl mx-auto px-4 sm:px-8 pb-12 ${isInlineAdmin ? 'pt-10 sm:pt-12' : 'pt-24'}`}>
          <button onClick={onBack} data-testid="gallery-back-button"
            className="flex items-center gap-2 text-zinc-400 hover:text-yellow-400 transition-colors mb-6 text-sm font-medium">
            <ArrowLeft size={16} /> Retour au site
          </button>
          <span className="inline-flex items-center gap-2 px-3 py-1 bg-yellow-500/15 border border-yellow-500/30 text-yellow-300 text-xs font-bold uppercase tracking-wider rounded-full mb-4">
            <Video size={12} /> KAZAJAH TV
          </span>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black mb-4 leading-[1.05]">
            La galerie <span className="bg-gradient-to-r from-yellow-400 via-amber-300 to-orange-400 bg-clip-text text-transparent">vidéo</span>
            <br />du label.
          </h1>
          <p className="text-zinc-400 text-base sm:text-lg max-w-2xl">
            Toutes les vidéos de nos artistes — clips, lives, sessions studio, performances. Un seul écran. Le label en mouvement.
          </p>
          <div className="flex flex-wrap items-center gap-4 mt-5 text-xs text-zinc-500">
            <span><span className="text-white font-bold">{allVideos.length}</span> vidéos</span>
            <span className="w-px h-4 bg-zinc-800" />
            <span><span className="text-white font-bold">{artistOptions.length - 1}</span> artistes</span>
            <span className="w-px h-4 bg-zinc-800" />
            <span><span className="text-white font-bold">{Object.keys(platformCounts).length}</span> plateformes</span>
          </div>
        </div>
      </div>

      {/* Vintage TV — featured player */}
      <section className="max-w-6xl mx-auto px-4 sm:px-8 pt-8 pb-4">
        <div className="text-center mb-5">
          <span className="inline-flex items-center gap-2 text-amber-400 text-xs font-bold uppercase tracking-[0.3em]">
            <Tv size={14} /> Le poste TV du label
          </span>
        </div>
        <VintageTV
          videos={allVideos}
          onSelectVideo={(v) => setPlaying(v)}
        />
      </section>

      {/* Filters */}
      <section className="max-w-7xl mx-auto px-4 sm:px-8 py-8">
        <div className="text-center mb-6">
          <h2 className="text-xl sm:text-2xl font-black text-white mb-1">Toutes les videos</h2>
          <p className="text-xs text-zinc-500">Parcourir, filtrer, regarder.</p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-5">
          <div className="relative md:col-span-1">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" />
            <input value={search} onChange={(e) => setSearch(e.target.value)}
              placeholder="Rechercher une vidéo, un artiste..."
              data-testid="gallery-search"
              className="w-full pl-9 pr-3 py-2.5 bg-zinc-900 border border-zinc-800 rounded-xl text-white text-sm focus:outline-none focus:border-yellow-500" />
          </div>
          <select value={activeArtist} onChange={(e) => setActiveArtist(e.target.value)}
            data-testid="gallery-artist-filter"
            className="px-3 py-2.5 bg-zinc-900 border border-zinc-800 rounded-xl text-white text-sm focus:outline-none focus:border-yellow-500">
            {artistOptions.map(([id, name]) => <option key={id} value={id}>{`${name}`}</option>)}
          </select>
          <select value={activePlatform} onChange={(e) => setActivePlatform(e.target.value)}
            data-testid="gallery-platform-filter"
            className="px-3 py-2.5 bg-zinc-900 border border-zinc-800 rounded-xl text-white text-sm focus:outline-none focus:border-yellow-500">
            <option value="all">Toutes plateformes</option>
            {Object.entries(platformCounts).map(([p, c]) => {
              const label = `${PLATFORM_META[p]?.label || p} (${c})`;
              return <option key={p} value={p}>{label}</option>;
            })}
          </select>
        </div>

        {/* Active filters */}
        {(activeArtist !== 'all' || activePlatform !== 'all' || search) && (
          <div className="flex items-center gap-2 mb-5 text-xs">
            <Filter size={12} className="text-yellow-500" />
            <span className="text-zinc-500">Filtres actifs:</span>
            {search && <span className="px-2 py-0.5 bg-zinc-800 text-zinc-300 rounded-full">&quot;{search}&quot;</span>}
            {activeArtist !== 'all' && (
              <span className="px-2 py-0.5 bg-zinc-800 text-zinc-300 rounded-full">
                {artistOptions.find(([id]) => id === activeArtist)?.[1]}
              </span>
            )}
            {activePlatform !== 'all' && (
              <span className={`px-2 py-0.5 rounded-full ${PLATFORM_META[activePlatform]?.bg} ${PLATFORM_META[activePlatform]?.color}`}>
                {PLATFORM_META[activePlatform]?.label}
              </span>
            )}
            <button onClick={() => { setSearch(''); setActiveArtist('all'); setActivePlatform('all'); }}
              className="ml-auto text-zinc-500 hover:text-yellow-400 transition-colors text-xs">Reset</button>
          </div>
        )}

        {/* Results */}
        {loading ? (
          <div className="flex items-center justify-center py-24">
            <Loader2 className="animate-spin text-yellow-500" size={32} />
          </div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-16 border border-dashed border-zinc-800 rounded-3xl"
            data-testid="gallery-empty-state">
            <Video size={32} className="text-zinc-700 mx-auto mb-3" />
            <p className="text-zinc-500 text-sm">Aucune vidéo ne correspond à ce filtre.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
            {filtered.map((v) => {
              const meta = PLATFORM_META[v.platform] || PLATFORM_META.other;
              const ytId = getYoutubeId(v.url);
              const thumb = getYoutubeThumb(v.url);
              return (
                <motion.div key={v.id} initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
                  className="group bg-zinc-900 border border-zinc-800 hover:border-yellow-500/40 rounded-2xl overflow-hidden cursor-pointer transition-colors relative"
                  data-testid={`gallery-video-${v.id}`}
                  onClick={() => ytId ? setPlaying(v) : window.open(v.url, '_blank', 'noopener,noreferrer')}>
                  {/* Admin pencil — top-right */}
                  {isInlineAdmin && v.videoIndex !== undefined && (
                    <button onClick={(e) => {
                        e.stopPropagation();
                        const a = mergedArtists.find(x => x.id === v.artistId);
                        if (a) setEditingVideo({ artist: a, videoIndex: v.videoIndex });
                      }}
                      data-testid={`gallery-video-edit-${v.id}`}
                      className="absolute top-2 right-2 z-20 p-1.5 rounded-lg bg-black/70 border border-red-500/40 text-red-300 hover:bg-red-500 hover:text-white transition-colors"
                      title="Editer cette video">
                      <Edit3 size={12} />
                    </button>
                  )}
                  <div className="relative aspect-video overflow-hidden bg-zinc-800">
                    {thumb ? (
                      <img src={thumb} alt={v.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                    ) : (
                      <div className={`w-full h-full flex items-center justify-center ${meta.bg}`}>
                        <Video size={36} className={meta.color} />
                      </div>
                    )}
                    <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent opacity-90 group-hover:opacity-100 transition-opacity" />
                    <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                      <span className="w-14 h-14 rounded-full bg-yellow-500 text-black flex items-center justify-center shadow-2xl">
                        {ytId ? <Play size={22} className="ml-1" /> : <ExternalLink size={20} />}
                      </span>
                    </div>
                    <span className={`absolute top-2 left-2 px-2 py-0.5 backdrop-blur-sm border text-[10px] font-bold uppercase tracking-wider rounded-full ${meta.bg} ${meta.color} ${meta.bd}`}>
                      {meta.label}
                    </span>
                  </div>
                  <div className="p-3">
                    <h3 className="font-bold text-white text-sm leading-snug line-clamp-2 mb-1">{v.title}</h3>
                    <p className="text-xs text-zinc-500 truncate">{v.artistName}</p>
                  </div>
                  <ShareVideoButton video={v} />
                </motion.div>
              );
            })}
          </div>
        )}
      </section>

      {/* Lightbox player */}
      <AnimatePresence>
        {editingVideo && (
          <InlineVideoEditor
            artist={editingVideo.artist}
            videoIndex={editingVideo.videoIndex}
            adminPwd={adminPwd}
            onClose={() => setEditingVideo(null)}
            onSaved={() => { setEditingVideo(null); fetchArtists(); }}
          />
        )}
      </AnimatePresence>
      <AnimatePresence>
        {playing && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            onClick={() => setPlaying(null)}
            className="fixed inset-0 z-[120] bg-black/90 backdrop-blur-sm flex items-center justify-center p-4"
            data-testid="gallery-lightbox">
            <motion.div initial={{ scale: 0.92, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.92, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
              className="relative w-full max-w-5xl">
              <button onClick={() => setPlaying(null)}
                data-testid="gallery-lightbox-close"
                className="absolute -top-12 right-0 text-white hover:text-yellow-400 flex items-center gap-1 text-sm font-bold">
                <X size={18} /> Fermer
              </button>
              <div className="aspect-video bg-black rounded-2xl overflow-hidden border border-yellow-500/30 shadow-2xl">
                <iframe
                  src={`https://www.youtube.com/embed/${getYoutubeId(playing.url)}?autoplay=1`}
                  title={playing.title}
                  className="w-full h-full"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; fullscreen"
                  allowFullScreen
                />
              </div>
              <div className="mt-4 flex items-center justify-between gap-4">
                <div>
                  <h3 className="text-white font-bold text-base">{playing.title}</h3>
                  <p className="text-zinc-400 text-xs">{playing.artistName}</p>
                </div>
                <a href={playing.url} target="_blank" rel="noopener noreferrer"
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-yellow-500 text-black text-xs font-bold rounded-full hover:bg-yellow-400 transition-colors">
                  Voir sur YouTube <ExternalLink size={12} />
                </a>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Quick add video modal */}
      <AnimatePresence>
        {quickAdd && (
          <QuickAddVideoModal artists={mergedArtists} adminPwd={adminPwd}
            onClose={() => setQuickAdd(false)}
            onSaved={() => { setQuickAdd(false); fetchArtists(); }} />
        )}
      </AnimatePresence>
    </div>
  );
}

/* ───────── Quick Add Video Modal ───────── */
function detectPlatform(url) {
  const u = (url || '').toLowerCase();
  if (u.includes('youtube.com') || u.includes('youtu.be')) return 'youtube';
  if (u.includes('facebook.com') || u.includes('fb.watch')) return 'facebook';
  if (u.includes('vimeo.com')) return 'vimeo';
  if (u.includes('tiktok.com')) return 'tiktok';
  return 'other';
}

function QuickAddVideoModal({ artists, adminPwd, onClose, onSaved }) {
  const [artistId, setArtistId] = useState(artists[0]?.id || '');
  const [title, setTitle] = useState('');
  const [url, setUrl] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const save = async () => {
    setError(''); setSaving(true);
    try {
      const artist = artists.find(a => a.id === artistId);
      if (!artist) { setError('Choisis un artiste'); setSaving(false); return; }
      if (!url.trim()) { setError('Colle une URL'); setSaving(false); return; }
      const newVideos = [...(artist.videos || []), {
        title: title.trim() || 'Nouvelle video',
        url: url.trim(),
        platform: detectPlatform(url),
      }];
      const fd = new FormData();
      fd.append('password', adminPwd);
      fd.append('name', artist.name || '');
      fd.append('videos_json', JSON.stringify(newVideos));
      const r = await fetch(`${API}/admin/artists/${artist.id}`, { method: 'PUT', body: fd });
      if (!r.ok) {
        const d = await r.json().catch(() => ({}));
        setError(d.detail || `Erreur ${r.status}`); setSaving(false); return;
      }
      onSaved();
    } catch (e) {
      setError(e.message || 'Erreur reseau');
    } finally { setSaving(false); }
  };

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      onClick={onClose}
      className="fixed inset-0 z-[125] bg-black/85 backdrop-blur-sm flex items-center justify-center p-4"
      data-testid="quick-add-video-modal">
      <motion.div initial={{ scale: 0.95, opacity: 0 }} animate={{ scale: 1, opacity: 1 }}
        onClick={(e) => e.stopPropagation()}
        className="bg-zinc-950 border border-red-500/40 rounded-2xl w-full max-w-lg shadow-2xl">
        <div className="flex items-center justify-between p-4 border-b border-zinc-800 bg-gradient-to-r from-red-500/15 to-transparent">
          <div>
            <p className="text-[10px] text-red-400 font-bold uppercase tracking-widest">Admin · KAZAJAH TV</p>
            <h3 className="text-base font-black text-white">Ajouter une nouvelle video</h3>
          </div>
          <button onClick={onClose} className="text-zinc-500 hover:text-white p-2"><X size={18} /></button>
        </div>
        <div className="p-5 space-y-3">
          <div>
            <p className="text-[10px] font-bold uppercase tracking-wider text-zinc-400 mb-1">Artiste</p>
            <select value={artistId} onChange={(e) => setArtistId(e.target.value)}
              data-testid="quick-add-artist"
              className="w-full px-3 py-2 bg-zinc-900 border border-zinc-800 rounded-lg text-white text-sm focus:outline-none focus:border-red-500">
              {artists.map(a => <option key={a.id} value={a.id}>{a.name} ({(a.videos || []).length} videos)</option>)}
            </select>
          </div>
          <div>
            <p className="text-[10px] font-bold uppercase tracking-wider text-zinc-400 mb-1">URL de la video</p>
            <input value={url} onChange={(e) => setUrl(e.target.value)}
              data-testid="quick-add-url"
              placeholder="https://www.youtube.com/watch?v=... (YouTube, Facebook, Vimeo, TikTok)"
              className="w-full px-3 py-2 bg-zinc-900 border border-zinc-800 rounded-lg text-white text-sm focus:outline-none focus:border-red-500" />
            {url && <p className="text-[10px] text-zinc-500 mt-1">Plateforme detectee : <span className="text-red-300 font-bold">{detectPlatform(url)}</span></p>}
          </div>
          <div>
            <p className="text-[10px] font-bold uppercase tracking-wider text-zinc-400 mb-1">Titre (optionnel)</p>
            <input value={title} onChange={(e) => setTitle(e.target.value)}
              data-testid="quick-add-title"
              placeholder="Si vide, sera nomme automatiquement"
              className="w-full px-3 py-2 bg-zinc-900 border border-zinc-800 rounded-lg text-white text-sm focus:outline-none focus:border-red-500" />
          </div>
          {error && <div className="p-2 bg-red-500/10 border border-red-500/30 rounded-lg text-xs text-red-300">{error}</div>}
        </div>
        <div className="flex justify-end gap-2 p-4 border-t border-zinc-800">
          <button onClick={onClose} className="px-4 py-2 bg-zinc-800 hover:bg-zinc-700 text-white rounded-lg text-xs">Annuler</button>
          <button onClick={save} disabled={saving || !url.trim()}
            data-testid="quick-add-save"
            className="px-4 py-2 bg-red-500 hover:bg-red-400 text-white font-bold rounded-lg text-xs flex items-center gap-1.5 disabled:opacity-50">
            {saving ? <Loader2 size={11} className="animate-spin" /> : <Save size={11} />} Ajouter
          </button>
        </div>
      </motion.div>
    </motion.div>
  );
}
